import React, { useContext, useEffect, useState } from 'react'
import { Button, Paper, useTheme, CircularProgress } from '@mui/material'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ExtensionIcon from '@mui/icons-material/Extension'
import hipersendService, { API_HS_NI_ENDPOINT } from '../../services/hipersend'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import Text from '../Text'

// ID da extensão GLA WAutenticator (via .env — muda entre a versão publicada e a
// carregada sem compactação, por isso não é fixo no código).
const EXTENSION_ID = process.env.REACT_APP_WAUTENTICATOR_EXT_ID || ''
const STORE_URL = EXTENSION_ID
  ? `https://chromewebstore.google.com/detail/${EXTENSION_ID}`
  : ''

// Fala com a extensão via externally_connectable. Só funciona no Chrome/Edge e
// se a extensão (que libera esta origem) estiver instalada.
function messageExtension(message) {
  return new Promise((resolve) => {
    try {
      const runtime = window.chrome && window.chrome.runtime
      if (!runtime || !runtime.sendMessage || !EXTENSION_ID) {
        resolve(null)
        return
      }
      runtime.sendMessage(EXTENSION_ID, message, (resp) => {
        if (window.chrome.runtime.lastError) {
          resolve(null)
          return
        }
        resolve(resp || null)
      })
    } catch (e) {
      resolve(null)
    }
  })
}

export default function DevicePasskey({ passkeyRequired = true }) {
  const theme = useTheme()
  const { getCredentials, phone, verifiedName } = useContext(ApiDataWrapperContext)
  const { api } = getCredentials()

  const [extInstalled, setExtInstalled] = useState(null) // null=checando | true | false
  const [step, setStep] = useState('idle') // idle | working | waiting | error
  const [errorMsg, setErrorMsg] = useState('')

  const checkExtension = async () => {
    const resp = await messageExtension({ type: 'HS_PING' })
    setExtInstalled(!!(resp && resp.ok))
  }

  useEffect(() => {
    checkExtension()
  }, [])

  const handleReconnect = async () => {
    setErrorMsg('')
    setStep('working')
    try {
      const { api: apiId, secret } = getCredentials()

      const data = await hipersendService.notificacoesInteligentes.mintBrowserAuthToken(apiId, secret)
      const token = data && data.token
      if (!token) {
        throw new Error('Não foi possível gerar o token de reconexão. Tente novamente.')
      }

      const bound = await messageExtension({
        type: 'HS_BIND',
        token,
        api: apiId,
        backend: API_HS_NI_ENDPOINT,
        label: verifiedName || phone || apiId
      })
      if (!bound || !bound.ok) {
        throw new Error('Não consegui falar com a extensão GLA WAutenticator. Verifique se ela está instalada e ativa.')
      }

      window.open('https://web.whatsapp.com', '_blank', 'noopener')
      setStep('waiting')
    } catch (e) {
      setErrorMsg((e && e.message) || 'Falha ao iniciar a reconexão.')
      setStep('error')
    }
  }

  const primary = theme.palette.primary.main

  return (
    <div className="relative min-h-[640px]">
      <Paper elevation={5}>
        <Paper elevation={0}>
          <div className="w-full mx-auto p-5 sm:p-8 xl:p-12">
            <div className="max-w-[720px]">
              {/* selo de estado */}
              <div
                className="inline-flex items-center space-x-2 px-3 py-1 rounded-full mb-5"
                style={{ backgroundColor: `${primary}1a`, color: primary }}
              >
                <VpnKeyIcon style={{ fontSize: 18 }} />
                <Text className="font-black text-xs" style={{ color: primary }}>
                  {passkeyRequired ? 'CHAVE DE ACESSO EXIGIDA' : 'CHAVE DE ACESSO · RECOMENDADO'}
                </Text>
              </div>

              <Text className="text-2xl md:text-3xl font-light leading-tight">
                {passkeyRequired
                  ? 'Este número passou a exigir chave de acesso'
                  : 'Conecte pelo WhatsApp Web, sem QR'}
              </Text>
              <Text className="mt-3" style={{ opacity: 0.75 }}>
                {passkeyRequired ? (
                  <>
                    O WhatsApp ativou a verificação por chave de acesso (passkey) para conectar este número, e o
                    QR Code tradicional não conclui mais a conexão. Reconecte pelo <b>GLA WAutenticator</b> em
                    poucos passos.
                  </>
                ) : (
                  <>
                    Conecte este número reaproveitando a sua sessão do <b>WhatsApp Web</b> — sem escanear QR.
                    Funciona mesmo quando a conta exige chave de acesso. Feito pelo <b>GLA WAutenticator</b> em
                    poucos passos.
                  </>
                )}
              </Text>

              <div className="mt-4 flex items-center space-x-2" style={{ opacity: 0.7 }}>
                <span className="block h-[6px] w-[6px] rounded-full" style={{ backgroundColor: primary }} />
                <Text className="text-sm">Número {phone ? `+${phone}` : ''} · instância {api}</Text>
              </div>

              {/* extensão não instalada */}
              {extInstalled === false && (
                <div
                  className="mt-8 p-5 rounded-lg"
                  style={{ border: `1px solid ${theme.palette.divider}` }}
                >
                  <div className="flex items-center space-x-2">
                    <ExtensionIcon style={{ color: primary }} />
                    <Text className="font-bold">Instale o Conector GLA WAutenticator</Text>
                  </div>
                  <Text className="text-sm mt-2 mb-4" style={{ opacity: 0.75 }}>
                    A extensão do navegador é o que captura a sua sessão do WhatsApp Web e reconecta o número
                    com segurança. Instale-a (uma vez) e recarregue esta página.
                  </Text>
                  <div className="flex flex-wrap gap-3">
                    {STORE_URL && (
                      <Button
                        variant="contained"
                        endIcon={<OpenInNewIcon />}
                        onClick={() => window.open(STORE_URL, '_blank', 'noopener')}
                      >
                        Instalar extensão
                      </Button>
                    )}
                    <Button variant="text" onClick={() => { setExtInstalled(null); checkExtension() }}>
                      Já instalei — verificar de novo
                    </Button>
                  </div>
                </div>
              )}

              {/* checando extensão */}
              {extInstalled === null && (
                <div className="mt-8 flex items-center space-x-3" style={{ opacity: 0.7 }}>
                  <CircularProgress size={18} />
                  <Text className="text-sm">Verificando a extensão…</Text>
                </div>
              )}

              {/* extensão OK */}
              {extInstalled === true && (
                <>
                  {(step === 'idle' || step === 'error') && (
                    <div className="mt-8">
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<VpnKeyIcon />}
                        onClick={handleReconnect}
                      >
                        Iniciar reconexão
                      </Button>
                      {step === 'error' && (
                        <div className="mt-4 flex items-start space-x-2" style={{ color: theme.palette.error.main }}>
                          <WarningAmberIcon style={{ fontSize: 20 }} />
                          <Text className="text-sm" style={{ color: theme.palette.error.main }}>{errorMsg}</Text>
                        </div>
                      )}
                    </div>
                  )}

                  {step === 'working' && (
                    <div className="mt-8 flex items-center space-x-3" style={{ opacity: 0.8 }}>
                      <CircularProgress size={20} />
                      <Text>Preparando a reconexão…</Text>
                    </div>
                  )}

                  {step === 'waiting' && (
                    <div className="mt-8">
                      <Paper elevation={0} style={{ border: `1px solid ${theme.palette.divider}` }}>
                        <div className="p-5">
                          <div className="flex items-center space-x-2">
                            <CircularProgress size={18} />
                            <Text className="font-bold">Aguardando você concluir no WhatsApp Web</Text>
                          </div>
                          <ol className="mt-4 space-y-3">
                            <li>
                              <Text className="text-sm">
                                <b>1.</b> Abrimos o WhatsApp Web numa nova aba. Faça o login e aprove a chave de
                                acesso no seu aparelho.
                              </Text>
                            </li>
                            <li>
                              <Text className="text-sm">
                                <b>2.</b> Clique no ícone do <b>GLA WAutenticator</b> e em “Conectar este número”.
                              </Text>
                            </li>
                            <li>
                              <Text className="text-sm">
                                <b>3.</b> Assim que conectar, esta tela atualiza sozinha.
                              </Text>
                            </li>
                          </ol>
                          <div
                            className="mt-4 flex items-start space-x-2 p-3 rounded"
                            style={{ backgroundColor: `${theme.palette.warning.main}1a`, color: theme.palette.warning.main }}
                          >
                            <WarningAmberIcon style={{ fontSize: 18 }} />
                            <Text className="text-sm" style={{ color: theme.palette.warning.main }}>
                              Não clique em “Sair” no WhatsApp Web — isso remove o dispositivo e desconecta o número.
                            </Text>
                          </div>
                          <div className="mt-4">
                            <Button variant="text" onClick={handleReconnect}>Abrir de novo / reenviar</Button>
                          </div>
                        </div>
                      </Paper>
                    </div>
                  )}
                </>
              )}

              {/* rodapé de confiança */}
              <div className="mt-8 flex items-start space-x-2" style={{ opacity: 0.6 }}>
                <CheckCircleIcon style={{ fontSize: 16 }} />
                <Text className="text-xs">
                  A extensão age só em web.whatsapp.com, no seu clique. As credenciais vão por conexão segura ao
                  seu backend Hipersend, com token de uso único — nada a terceiros.
                </Text>
              </div>
            </div>
          </div>
        </Paper>
      </Paper>
    </div>
  )
}
